import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface ChallengeTrackingContextType {
  trackAction: (actionType: string, increment?: number, actionTarget?: string) => Promise<void>;
  trackLogin: () => Promise<void>;
  trackChatStart: () => Promise<void>;
  trackMessageSend: () => Promise<void>;
  trackToolUse: (toolId: string) => Promise<void>;
  trackPromptCreate: () => Promise<void>;
}

const ChallengeTrackingContext = createContext<ChallengeTrackingContextType | null>(null);

export const useChallengeTracking = () => {
  const context = useContext(ChallengeTrackingContext);
  if (!context) {
    // Return a no-op version when outside provider
    return {
      trackAction: async () => {},
      trackLogin: async () => {},
      trackChatStart: async () => {},
      trackMessageSend: async () => {},
      trackToolUse: async () => {},
      trackPromptCreate: async () => {},
    };
  }
  return context;
};

export const ChallengeTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const loginTrackedRef = useRef(false);
  const trackedActionsToday = useRef<Set<string>>(new Set());

  // Reset daily tracking cache at midnight
  useEffect(() => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const msUntilMidnight = midnight.getTime() - now.getTime();

    const timeout = setTimeout(() => {
      trackedActionsToday.current.clear();
    }, msUntilMidnight);

    return () => clearTimeout(timeout);
  }, []);

  const trackAction = useCallback(async (
    actionType: string,
    increment: number = 1,
    actionTarget?: string
  ) => {
    if (!user) return;

    try {
      const today = new Date().toISOString().split('T')[0];

      // Fetch active challenges
      const { data: challenges, error: challengesError } = await supabase
        .from('daily_challenges')
        .select('*')
        .eq('is_active', true);

      if (challengesError) throw challengesError;

      // Fetch user's progress for today
      const { data: progress, error: progressError } = await supabase
        .from('user_challenge_progress')
        .select('*')
        .eq('user_id', user.id)
        .gte('expires_at', new Date().toISOString());

      if (progressError) throw progressError;

      // Find matching challenges
      const matchingChallenges = challenges?.filter(c =>
        c.action_type === actionType &&
        (!c.action_target || c.action_target === actionTarget)
      ) || [];

      for (const challenge of matchingChallenges) {
        const existingProgress = progress?.find(p => p.challenge_id === challenge.id);

        // Skip if already completed
        if (existingProgress?.is_completed) continue;

        const currentProgress = existingProgress?.current_progress || 0;
        const newProgress = Math.min(currentProgress + increment, challenge.target_count || 1);
        const isNowCompleted = newProgress >= (challenge.target_count || 1);

        // Calculate expiry based on challenge type
        const expiresAt = challenge.challenge_type === 'daily'
          ? new Date(new Date().setHours(23, 59, 59, 999)).toISOString()
          : (() => {
              const now = new Date();
              const daysUntilSunday = 7 - now.getDay();
              now.setDate(now.getDate() + daysUntilSunday);
              now.setHours(23, 59, 59, 999);
              return now.toISOString();
            })();

        if (existingProgress?.id) {
          // Update existing progress
          await supabase
            .from('user_challenge_progress')
            .update({
              current_progress: newProgress,
              is_completed: isNowCompleted,
              completed_at: isNowCompleted ? new Date().toISOString() : null,
            })
            .eq('id', existingProgress.id);
        } else {
          // Create new progress entry
          await supabase
            .from('user_challenge_progress')
            .insert({
              user_id: user.id,
              challenge_id: challenge.id,
              current_progress: newProgress,
              is_completed: isNowCompleted,
              completed_at: isNowCompleted ? new Date().toISOString() : null,
              assigned_date: today,
              expires_at: expiresAt,
            });
        }

        if (isNowCompleted) {
          toast.success(`🎯 Challenge Complete: ${challenge.title}! +${challenge.xp_reward} XP`);
        }
      }
    } catch (error) {
      console.error('Error tracking challenge progress:', error);
    }
  }, [user]);

  const trackLogin = useCallback(async () => {
    if (!user || loginTrackedRef.current) return;
    loginTrackedRef.current = true;
    await trackAction('login');
  }, [user, trackAction]);

  const trackChatStart = useCallback(async () => {
    await trackAction('chat_start');
  }, [trackAction]);

  const trackMessageSend = useCallback(async () => {
    await trackAction('message_send');
  }, [trackAction]);

  const trackToolUse = useCallback(async (toolId: string) => {
    await trackAction('tool_use', 1, 'suite');
    // Also track for unique tools challenge
    const uniqueKey = `unique_tool_${toolId}`;
    if (!trackedActionsToday.current.has(uniqueKey)) {
      trackedActionsToday.current.add(uniqueKey);
      await trackAction('unique_tools');
    }
  }, [trackAction]);

  const trackPromptCreate = useCallback(async () => {
    await trackAction('prompt_create');
  }, [trackAction]);

  // Track login on mount when user exists
  useEffect(() => {
    if (user && !loginTrackedRef.current) {
      trackLogin();
    }
  }, [user, trackLogin]);

  // Check and award weekly freeze token
  useEffect(() => {
    if (!user) return;

    const checkWeeklyFreezeReward = async () => {
      try {
        const { data: streakData } = await supabase
          .from('user_streaks')
          .select('current_streak, freeze_tokens, updated_at')
          .eq('user_id', user.id)
          .single();

        if (!streakData) return;

        // Only award if streak is 7+ days
        if (streakData.current_streak < 7) return;

        // Check if it's been at least 7 days since last token award
        const lastUpdate = new Date(streakData.updated_at);
        const now = new Date();
        const daysSinceUpdate = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));

        // Award token if 7+ days since last update and streak is maintained
        if (daysSinceUpdate >= 7 && streakData.freeze_tokens < 5) {
          await supabase
            .from('user_streaks')
            .update({ 
              freeze_tokens: Math.min(streakData.freeze_tokens + 1, 5) 
            })
            .eq('user_id', user.id);

          toast.success('🧊 You earned a freeze token for maintaining your 7+ day streak!');
        }
      } catch (error) {
        console.error('Error checking weekly freeze reward:', error);
      }
    };

    checkWeeklyFreezeReward();
  }, [user]);

  return (
    <ChallengeTrackingContext.Provider value={{
      trackAction,
      trackLogin,
      trackChatStart,
      trackMessageSend,
      trackToolUse,
      trackPromptCreate,
    }}>
      {children}
    </ChallengeTrackingContext.Provider>
  );
};
